import { Fragment } from 'react';
import { cn } from '@/lib/utils';
import { LifecycleBadge } from './LifecycleBadge';

export interface IdentityField {
  label: string;
  value: string;
  /** true → big monospace (Document No. / version); false → smaller semibold (Market). */
  mono?: boolean;
  /** A placeholder rather than a fact (e.g. "Assigned on promote"): quieter colour and weight. */
  muted?: boolean;
}

const LABEL_CLS = 'text-[10.5px] font-semibold uppercase tracking-[0.09em] text-muted';
const VALUE_MONO = 'font-mono text-[19px] font-bold tracking-tight text-navy';
const VALUE_TEXT = 'text-[15px] font-semibold text-navy';

/**
 * The document's key facts as a prominent row — Document No., version, market, etc. —
 * with the lifecycle badge on the right. One definition shared by the document page,
 * the editor, and the review page so the three never drift.
 */
export function DocumentIdentityStrip({
  fields,
  state,
  className,
}: {
  fields: IdentityField[];
  state: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-2xl border border-line bg-white px-5 py-4 shadow-card',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
        {fields.map((f, i) => (
          <Fragment key={f.label}>
            {i > 0 && <span className="hidden h-6 w-px bg-line sm:block" aria-hidden />}
            <div className="flex items-center gap-3">
              <span className={LABEL_CLS}>{f.label}</span>
              <span className={cn(f.mono ? VALUE_MONO : VALUE_TEXT, f.muted && 'font-medium text-muted')}>{f.value}</span>
            </div>
          </Fragment>
        ))}
      </div>
      <LifecycleBadge state={state} />
    </div>
  );
}
